import React, { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Activity, Download, Filter, User, Clock } from "lucide-react";
import { toast } from "sonner";

const MODULE_LABELS = {
  dashboard: "Dashboard", recepcion: "Recepción", laboratorio: "Laboratorio",
  correcciones: "Correcciones", produccion: "Producción", trazabilidad: "Trazabilidad",
  calculadora: "Calculadora", inventario: "Inventario", ventas: "Ventas",
  clientes: "Clientes", cobranza: "Cobranza", insumos: "Insumos",
  parametros: "Parámetros", usuarios: "Usuarios", auditoria: "Auditoría",
  otro: "Otro",
};

const ACTION_TONE = {
  create: "bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300",
  update: "bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300",
  delete: "bg-red-100 dark:bg-red-950/40 text-red-800 dark:text-red-300",
  read: "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300",
};

const ACTION_LABEL = { create: "Crear", update: "Modificar", delete: "Eliminar", read: "Leer" };

export default function Auditoria() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, today: 0, top_users_week: [], modules: [], actions: [] });
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ user_id: "", module: "", action: "", date_from: "", date_to: "" });
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    params.append("limit", "500");
    const [it, sum] = await Promise.all([
      api.get(`/audit-logs?${params.toString()}`),
      api.get("/audit-logs/summary"),
    ]);
    setItems(it.data);
    setSummary(sum.data);
  };

  const loadUsers = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch {
      // user without permiso usuarios.view — leave empty, filter shows text input option
    }
  };

  useEffect(() => { load(); loadUsers(); /* eslint-disable-next-line */ }, []);

  const applyFilters = () => load();
  const clearFilters = () => { setFilters({ user_id: "", module: "", action: "", date_from: "", date_to: "" }); setTimeout(load, 0); };

  const download = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    const token = localStorage.getItem("lp_token");
    fetch(`${API}/audit-logs/export.csv?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `bitacora_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Bitácora descargada");
      })
      .catch(() => toast.error("Error al descargar"));
  };

  return (
    <div className="max-w-screen-2xl mx-auto p-6 lg:p-8 space-y-6" data-testid="auditoria-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-outfit font-bold text-3xl tracking-tight text-stone-900 dark:text-stone-100">Bitácora / Auditoría</h1>
          <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">Quién hizo qué, cuándo y desde dónde. Se registran todas las operaciones de creación, modificación y eliminación.</p>
        </div>
        <Button onClick={download} data-testid="export-csv-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">
          <Download className="w-4 h-4 mr-1.5" /> Exportar CSV
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPI icon={Activity} label="Total de eventos" value={summary.total.toLocaleString("es-MX")} sub="En todo el sistema" />
        <KPI icon={Clock} label="Eventos de hoy" value={summary.today.toLocaleString("es-MX")} sub="Actividad del día" />
        <Card className="p-5 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
          <div className="text-xs font-outfit font-semibold uppercase tracking-[0.1em] text-stone-500 dark:text-stone-400 mb-2">Top usuarios · últimos 7 días</div>
          {summary.top_users_week?.length === 0 ? (
            <div className="text-sm text-stone-500">Sin actividad reciente.</div>
          ) : (
            <div className="space-y-1.5">
              {summary.top_users_week?.slice(0, 5).map((u, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-1.5 text-stone-800 dark:text-stone-200 truncate"><User className="w-3.5 h-3.5 text-stone-400" /> {u.user_name}</div>
                  <span className="tabular-nums font-semibold text-[#1E3A8A] dark:text-blue-400">{u.count}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card className="p-4 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-stone-500" />
          <div className="font-outfit font-semibold text-sm text-stone-900 dark:text-stone-100">Filtros</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
          <Select value={filters.user_id || "_all"} onValueChange={(v) => setFilters({ ...filters, user_id: v === "_all" ? "" : v })}>
            <SelectTrigger data-testid="filter-user"><SelectValue placeholder="Usuario" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Todos los usuarios</SelectItem>
              {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.module || "_all"} onValueChange={(v) => setFilters({ ...filters, module: v === "_all" ? "" : v })}>
            <SelectTrigger data-testid="filter-module"><SelectValue placeholder="Módulo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Todos los módulos</SelectItem>
              {summary.modules.map((m) => <SelectItem key={m} value={m}>{MODULE_LABELS[m] || m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.action || "_all"} onValueChange={(v) => setFilters({ ...filters, action: v === "_all" ? "" : v })}>
            <SelectTrigger data-testid="filter-action"><SelectValue placeholder="Acción" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Todas las acciones</SelectItem>
              {["create", "update", "delete"].map((a) => <SelectItem key={a} value={a}>{ACTION_LABEL[a]}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} placeholder="Desde" data-testid="filter-from" />
          <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} placeholder="Hasta" data-testid="filter-to" />
          <div className="flex gap-1">
            <Button onClick={applyFilters} className="flex-1 bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="apply-filters-btn">Aplicar</Button>
            <Button onClick={clearFilters} variant="outline" className="flex-shrink-0">Limpiar</Button>
          </div>
        </div>
      </Card>

      <Card className="border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-stone-200 dark:border-stone-800">
          <h3 className="font-outfit font-semibold text-stone-900 dark:text-stone-100 text-sm">{items.length} eventos</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-100 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
              <tr>{["Fecha y hora", "Usuario", "Módulo", "Acción", "Entidad", "IP", "Descripción"].map((h) => <th key={h} className="px-3 py-2.5 text-left text-[10px] font-outfit font-semibold uppercase tracking-wider text-stone-600 dark:text-stone-400">{h}</th>)}</tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-stone-500">Sin registros con los filtros aplicados.</td></tr>
              ) : items.map((a) => (
                <tr key={a.id} onClick={() => setSelected(a)} className="border-b border-stone-100 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-900/50 cursor-pointer" data-testid={`audit-row-${a.id}`}>
                  <td className="px-3 py-2 text-xs tabular-nums font-mono text-stone-600 dark:text-stone-400 whitespace-nowrap">{a.timestamp?.slice(0, 19).replace("T", " ")}</td>
                  <td className="px-3 py-2 text-sm">
                    <div className="font-medium text-stone-900 dark:text-stone-100">{a.user_name}</div>
                    <div className="text-[10px] text-stone-500 dark:text-stone-400">{a.user_email}</div>
                  </td>
                  <td className="px-3 py-2 text-xs"><span className="inline-block px-2 py-0.5 rounded bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 font-medium">{MODULE_LABELS[a.module] || a.module}</span></td>
                  <td className="px-3 py-2"><span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-outfit font-semibold ${ACTION_TONE[a.action] || ACTION_TONE.read}`}>{ACTION_LABEL[a.action] || a.action}</span></td>
                  <td className="px-3 py-2 text-xs text-stone-700 dark:text-stone-300">{a.entity_folio || a.entity_type || "—"}</td>
                  <td className="px-3 py-2 text-xs font-mono text-stone-600 dark:text-stone-400">{a.ip || "—"}</td>
                  <td className="px-3 py-2 text-xs text-stone-600 dark:text-stone-400 max-w-md truncate">{a.description || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {selected && <DetailPanel item={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

const KPI = ({ icon: Icon, label, value, sub }) => (
  <Card className="p-5 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs font-outfit font-semibold uppercase tracking-[0.1em] text-stone-500 dark:text-stone-400">{label}</div>
        <div className="mt-2 font-outfit font-semibold text-2xl text-stone-900 dark:text-stone-100 tabular-nums">{value}</div>
        {sub && <div className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">{sub}</div>}
      </div>
      <div className="w-9 h-9 rounded-md bg-[#1E3A8A]/10 text-[#1E3A8A] dark:text-blue-400 flex items-center justify-center"><Icon className="w-4 h-4" strokeWidth={1.75} /></div>
    </div>
  </Card>
);

const DetailPanel = ({ item, onClose }) => (
  <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
    <Card className="max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-2xl" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-outfit font-semibold text-lg text-stone-900 dark:text-stone-100">Evento #{item.id.slice(0, 8)}</div>
          <div className="text-xs text-stone-500 dark:text-stone-400 font-mono">{item.timestamp}</div>
        </div>
        <button onClick={onClose} className="text-stone-400 hover:text-stone-700 dark:hover:text-stone-200 text-xl">×</button>
      </div>
      <div className="space-y-3 text-sm">
        <Row k="Usuario" v={`${item.user_name} (${item.user_email})`} />
        <Row k="Rol" v={item.user_role} />
        <Row k="Módulo" v={MODULE_LABELS[item.module] || item.module} />
        <Row k="Acción" v={ACTION_LABEL[item.action] || item.action} />
        <Row k="Entidad" v={`${item.entity_type || "—"}${item.entity_folio ? ` · ${item.entity_folio}` : ""}${item.entity_id ? ` · id ${item.entity_id.slice(0, 8)}` : ""}`} />
        <Row k="IP" v={item.ip || "—"} />
        <Row k="User agent" v={item.user_agent || "—"} />
        <Row k="Descripción" v={item.description || "—"} />
        {item.before && (
          <div>
            <div className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-500 dark:text-stone-400 mb-1">Antes</div>
            <pre className="text-xs bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded p-2 overflow-x-auto max-h-40">{JSON.stringify(item.before, null, 2)}</pre>
          </div>
        )}
        {item.after && (
          <div>
            <div className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-500 dark:text-stone-400 mb-1">Después</div>
            <pre className="text-xs bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 rounded p-2 overflow-x-auto max-h-40">{JSON.stringify(item.after, null, 2)}</pre>
          </div>
        )}
      </div>
    </Card>
  </div>
);

const Row = ({ k, v }) => (
  <div className="grid grid-cols-3 gap-3 py-1.5 border-b border-stone-100 dark:border-stone-800 last:border-0">
    <div className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-500 dark:text-stone-400">{k}</div>
    <div className="col-span-2 text-sm text-stone-800 dark:text-stone-200 break-words">{v}</div>
  </div>
);
